import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

/**
 * EnhancedButtonToggle - A reusable button-style toggle component with dynamic styling and multiple options
 *
 * Features:
 * - Support for multiple toggle options (2 or more)
 * - Size variants (sm, md, lg)
 * - Color variants for active states
 * - Smooth transitions and animations
 * - Dark mode support
 * - Accessible button controls
 *
 * @param {Object} props - Component props
 * @param {Array} props.options - Array of option objects with { value, label, colorScheme, icon }
 * @param {*} props.value - Currently selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} [props.size="md"] - Size variant (sm, md, lg)
 * @param {string} [props.className] - Additional CSS classes for container
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled
 * @param {boolean} [props.fullWidth=false] - Whether buttons should fill available width
 */
export function EnhancedButtonToggle({
  options = [],
  value,
  onChange,
  size = "md",
  className = "",
  disabled = false,
  fullWidth = false,
}) {
  // Size variants - ALIGNED WITH EnhancedSearchBox and EnhancedSortOption
  const sizeClasses = {
    sm: {
      container: "p-0.5",
      button: "h-8 px-2 text-xs", // h-8 to match other components
      rounded: "rounded",
      icon: "w-3 h-3", // Icon size for small variant
    },
    md: {
      container: "p-1",
      button: "h-10 px-3 text-sm", // h-10 to match other components
      rounded: "rounded-md",
      icon: "w-4 h-4", // Icon size for medium variant
    },
    lg: {
      container: "p-1.5",
      button: "h-12 px-4 text-base", // h-12 to match other components
      rounded: "rounded-lg",
      icon: "w-5 h-5", // Icon size for large variant
    },
  };

  // Color schemes for different option types
  const colorSchemes = {
    primary: {
      active:
        "bg-white text-indigo-700 shadow-sm ring-1 ring-indigo-200 dark:bg-gray-700 dark:text-indigo-400 dark:ring-indigo-800",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
    success: {
      active:
        "bg-white text-green-700 shadow-sm ring-1 ring-green-200 dark:bg-gray-700 dark:text-green-400 dark:ring-green-800",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
    warning: {
      active:
        "bg-white text-orange-700 shadow-sm ring-1 ring-orange-200 dark:bg-gray-700 dark:text-orange-400 dark:ring-orange-800",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
    danger: {
      active:
        "bg-white text-red-700 shadow-sm ring-1 ring-red-200 dark:bg-gray-700 dark:text-red-400 dark:ring-red-800",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
    neutral: {
      active:
        "bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
    info: {
      active:
        "bg-white text-blue-700 shadow-sm ring-1 ring-blue-200 dark:bg-gray-700 dark:text-blue-400 dark:ring-blue-800",
      inactive: "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
    },
  };

  const currentSize = sizeClasses[size];

  const handleClick = optionValue => {
    if (!disabled && onChange) {
      onChange(optionValue);
    }
  };

  return (
    <div
      className={classNames(
        "flex rounded-lg bg-gray-100 dark:bg-gray-800 transition-colors duration-200",
        currentSize.container,
        fullWidth && "w-full",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      {options.map((option, index) => {
        const isActive = value === option.value;
        const colorScheme = colorSchemes[option.colorScheme] || colorSchemes.primary;
        const buttonClasses = isActive ? colorScheme.active : colorScheme.inactive;

        return (
          <button
            key={option.value ?? index}
            type="button"
            onClick={() => handleClick(option.value)}
            disabled={disabled}
            className={classNames(
              currentSize.rounded,
              currentSize.button,
              "font-medium transition-all duration-200 flex items-center justify-center",
              buttonClasses,
              disabled ? "cursor-not-allowed" : "cursor-pointer",
              fullWidth && "flex-1",
            )}
            aria-pressed={isActive}
            aria-label={option.ariaLabel || option.label}
          >
            {option.icon && (
              <span className={classNames("mr-2", currentSize.icon)}>
                {React.isValidElement(option.icon)
                  ? option.icon
                  : React.createElement(option.icon, { className: currentSize.icon })}
              </span>
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

EnhancedButtonToggle.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      colorScheme: PropTypes.oneOf(["primary", "success", "warning", "danger", "neutral", "info"]),
      ariaLabel: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]), // Support for icon nodes and components
    }),
  ).isRequired,
  value: PropTypes.any.isRequired,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default EnhancedButtonToggle;
